import React from 'react';
import { observer } from 'mobx-react';
import { useHistory, useParams } from 'react-router-dom';

import { StandardPage } from 'ui/base/page/standard_page';
import { BackButton } from 'ui/base/button/back_button';
import { Theme, ThemedStyles, useStyles } from 'ui/base/theme';
import { FormSubmitEvent } from 'ui/base/types';

import { PlainButton } from 'ui/public/button';
import { LabeledInput, TextInput } from 'ui/public/input';
import { ErrorMessage } from 'ui/public/error_message/error_message';

import { TimezoneInput } from 'ui/standard/input/timezone_input/timezone_input';
import { ShieldIconSelector } from 'ui/standard/image/shield_icon_selector/shield_icon_selector';

import { ResultKind } from 'services/base/types';
import { IAccountsDomain, Pangolin } from 'services/accounts/types';
import { useAccountsDomain } from 'services/accounts/accounts_domain';

import { Redirect } from 'pages/core/redirect/redirect';

import BaseStyles from './edit_pangolin.module.css';
import EditPangolinMessages from './edit_pangolin_messages';
import { EditPangolinStore } from './edit_pangolin_store';

type EditPangolinStyles = {
  save: string;
  headerContainer: string;
  avatarContainer: string;
  avatar: string;
  fillOutContainer: string;
};

const themedStyles: ThemedStyles<EditPangolinStyles> = {
  [Theme.DAYLIGHT]: {
    save: BaseStyles.daylightSave,
    headerContainer: BaseStyles.headerContainer,
    avatarContainer: BaseStyles.pangolinAvatar,
    avatar: BaseStyles.avatar,
    fillOutContainer: BaseStyles.fillOutContainer,
  },
  [Theme.MIDNIGHT]: {
    save: BaseStyles.midnightSave,
    headerContainer: BaseStyles.headerContainer,
    avatarContainer: BaseStyles.pangolinAvatar,
    avatar: BaseStyles.avatar,
    fillOutContainer: BaseStyles.fillOutContainer,
  },
};

type EditPangolinProps = {
  accounts: IAccountsDomain;
  pangolin: Pangolin;
};

export const BaseEditPangolin = observer(({ accounts, pangolin }: EditPangolinProps) => {
  const history = useHistory();
  const styles = useStyles(themedStyles);

  const [store, setStore] = React.useState(() => new EditPangolinStore(pangolin));
  React.useEffect(() => { setStore(new EditPangolinStore(pangolin)); }, [pangolin]);

  const handleSave = React.useCallback(async(event: FormSubmitEvent) => {
    event.preventDefault();
    const result = await store.busy.perform(() => store.handleSave(accounts));
    if (result.kind === ResultKind.OK) {
      history.goBack();
    }
  }, [store, accounts, history]);

  return (
    <StandardPage>
      <form onSubmit={handleSave}>
        <div className={styles.headerContainer}>
          <BackButton/>
          <PlainButton type='submit'
            busy={store.busy} reserveBusyIconArea={true}
            className={styles.save}>{EditPangolinMessages.save()}</PlainButton>
        </div>
        <div className={styles.avatarContainer}>
          <ShieldIconSelector store={store.shieldIcon}/>
        </div>
        <div className={styles.fillOutContainer}>
          <LabeledInput label={EditPangolinMessages.nickname()}>
            <TextInput store={store.nickname}/>
          </LabeledInput>
          <ErrorMessage message={store.nicknameError}/>
          <LabeledInput label={EditPangolinMessages.timezone()}>
            <TimezoneInput store={store.timezone} placeholder='Timezone'/>
          </LabeledInput>
        </div>
      </form>
    </StandardPage>
  );
});

export const EditPangolin = observer(() => {
  const { pangolinID } = useParams<{ pangolinID: string }>();
  if (!pangolinID) return <Redirect />;

  const accounts = useAccountsDomain();

  const pangolin = accounts.pangolins.get(pangolinID);
  if (!pangolin) return <Redirect />;

  return (
    <BaseEditPangolin
      accounts={accounts}
      pangolin={pangolin}
    />
  );
});
